import { forwardRef } from 'react';
import { Helmet } from 'react-helmet';

import { makeStyles } from '@material-ui/core';

import classNames from 'classnames';

import { configApiRef, useApi } from '@backstage/core-plugin-api';

import { ScrollTopButtonMui } from '../ScrollTopButtonMui';
import { BackstagePage } from './BackstagePage';
import { Content } from './Content';
import { Header } from './Header';
import { HeaderContent } from './HeaderContent';
import { PageLayoutProps } from './PageLayout';
import { SidebarContent } from './SidebarContent';

const useStyles = makeStyles(theme => ({
  content: { paddingBottom: theme.spacing(12) },
  // min-height keeps the pageContent fr track from growing past the page —
  // fr tracks have an auto minimum, which would force the page to scroll.
  fullViewportContent: {
    minHeight: 0,
    overflow: 'hidden',
    paddingBottom: theme.spacing(4),
  },
  scrollableFullViewportContent: {
    overflow: 'auto',
  },
}));

export const PageLayoutMui = forwardRef<HTMLDivElement, PageLayoutProps>(
  (
    {
      children,
      floatingHeader,
      sideBarRight,
      sideBarLeft,
      title,
      enableMargin,
      headerBreadcrumbs,
      headerTabs,
      headerContent,
      stickyHeader,
      stickyHeaderTabs,
      headerTabsRef,
      isLoading,
      pageTitleOverride,
      scrollButton = false,
      narrow,
      noPadding,
      subheader,
      fullViewport,
      contentOverflow = 'hidden',
    },
    ref,
  ) => {
    const classes = useStyles();

    const hasHeader =
      title ||
      headerBreadcrumbs ||
      headerTabs ||
      headerContent ||
      subheader ||
      isLoading;

    const configApi = useApi(configApiRef);
    const appTitle = configApi.getOptionalString('app.title') || 'Backstage';

    return (
      <BackstagePage ref={ref} fullViewport={fullViewport}>
        {floatingHeader}
        {scrollButton && <ScrollTopButtonMui />}
        {hasHeader ? (
          <Header
            enableMargin={enableMargin}
            title={title}
            subheader={subheader}
            isLoading={isLoading}
            pageTitleOverride={pageTitleOverride}
            narrow={narrow}
            headerBreadcrumbs={headerBreadcrumbs}
            headerTabs={headerTabs}
            stickyHeader={stickyHeader}
            stickyHeaderTabs={stickyHeaderTabs}
            headerTabsRef={headerTabsRef}
          >
            {headerContent ? (
              <HeaderContent>{headerContent}</HeaderContent>
            ) : null}
          </Header>
        ) : (
          pageTitleOverride && (
            <Helmet defaultTitle={`${pageTitleOverride} | ${appTitle}`} />
          )
        )}

        <Content
          narrow={narrow}
          className={classNames(noPadding ? undefined : classes.content, {
            [classes.fullViewportContent]: fullViewport,
            [classes.scrollableFullViewportContent]:
              fullViewport && contentOverflow === 'auto',
          })}
          noPadding={noPadding}
        >
          {sideBarLeft || sideBarRight ? (
            <SidebarContent
              sideBarLeft={sideBarLeft}
              sideBarRight={sideBarRight}
            >
              {children}
            </SidebarContent>
          ) : (
            children
          )}
        </Content>
      </BackstagePage>
    );
  },
);
PageLayoutMui.displayName = 'PageLayoutMui';
